import React, {
  useState,
  forwardRef,
  useRef,
  useLayoutEffect,
  useEffect,
} from "react";
import styles from "./styles.module.css";

const CustomInput = forwardRef(
  (
    {
      type = "text",
      placeholder = "",
      label = "",
      name = "",
      value = "",
      onChange,
      onBlur,
      onFocus,
      disabled = false,
      required = false,
      error = "",
      helperText = "",
      variant = "default", // default, outlined, filled
      size = "small", // small, medium, large
      className = "",
      startIcon = null,
      endIcon = null,
      mask,
      maskPattern = "99/99/9999", // Default pattern for backward compatibility
      ...rest
    },
    ref
  ) => {
    const [isFocused, setIsFocused] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [cursor, setCursor] = useState(0);
    const inputLocalRef = useRef(null);

    // Always use internal state for display value to avoid conflicts
    const [displayValue, setDisplayValue] = useState("");
    const isControlled = onChange !== undefined;

    // Merge forwarded ref with local ref
    const setRefs = (node) => {
      inputLocalRef.current = node;
      if (typeof ref === "function") {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    };

    // Dynamic mask helpers using placeholder approach
    const placeholderChar = "_";

    const getFullPlaceholder = () => {
      return maskPattern.replace(/9/g, placeholderChar);
    };

    const applyMask = (digits) => {
      let masked = "";
      let i = 0;
      for (let char of maskPattern) {
        if (char === "9") {
          masked += i < digits.length ? digits[i++] : placeholderChar;
        } else {
          masked += char;
        }
      }
      return masked;
    };

    // Extract digits from any value (formatted or raw)
    const extractDigits = (val) => {
      if (!val) return "";
      return String(val).replace(/\D/g, "");
    };

    // Apply mask to any raw value (for programmatic updates)
    const formatValueWithMask = (rawValue) => {
      if (!mask || !rawValue) return rawValue || "";
      const digitCount = (maskPattern.match(/9/g) || []).length;
      const digits = extractDigits(rawValue).substring(0, digitCount);
      if (digits.length === 0) return "";
      return applyMask(digits);
    };

    // Check if a value is already properly formatted
    const isValueFormatted = (val) => {
      if (!mask || !val) return false;
      const digits = extractDigits(val);
      const formatted = formatValueWithMask(digits);
      return val === formatted;
    };

    // Initialize and sync display value with external value changes
    // Track if we're currently typing to avoid interfering with user input
    const isTypingRef = useRef(false);

    useEffect(() => {
      // Don't sync during active typing - let handleChange manage it
      if (isTypingRef.current) {
        isTypingRef.current = false;
        return;
      }

      if (mask) {
        // For masked inputs, always format the value
        const formatted = formatValueWithMask(value);
        setDisplayValue(formatted);

        // If controlled and value isn't formatted, emit formatted version
        // But only on initial load or when value changes externally (not from typing)
        if (isControlled && value && !isValueFormatted(value)) {
          // Notify parent with formatted value on mount/value change
          const digits = extractDigits(value);
          if (digits.length > 0) {
            const formattedValue = formatValueWithMask(digits);
            // Use setTimeout to avoid state updates during render
            setTimeout(() => {
              onChange?.({
                target: {
                  name,
                  value: formattedValue,
                },
              });
            }, 0);
          }
        }
      } else {
        // For non-masked inputs, use value as-is
        setDisplayValue(value || "");
      }
    }, [value, mask, maskPattern]);

    const handleChange = (e) => {
      const newValue = e.target.value;

      // Only apply masking when mask prop is provided
      if (mask) {
        // Mark that we're typing to prevent useEffect interference
        isTypingRef.current = true;

        const digitCount = (maskPattern.match(/9/g) || []).length;
        const digits = extractDigits(newValue).substring(0, digitCount);
        const newValueMasked = applyMask(digits);
        setCursor(e.target.selectionStart || 0);
        setDisplayValue(newValueMasked);

        if (onChange) {
          onChange({
            target: {
              name,
              value: newValueMasked,
            },
          });
        }
        return;
      }

      setDisplayValue(newValue);
      if (onChange) {
        onChange(e);
      }
    };

    const handleFocus = (e) => {
      setIsFocused(true);
      if (onFocus) {
        onFocus(e);
      }
    };

    const handleBlur = (e) => {
      setIsFocused(false);
      if (onBlur) {
        onBlur(e);
      }
    };

    const togglePasswordVisibility = () => {
      setShowPassword(!showPassword);
    };

    // Password fields render readOnly until first focus, then have the
    // attribute removed via direct DOM mutation (not React state) — this
    // stops password managers from auto-filling on page load without
    // fighting React's reconciliation on later re-renders, since the
    // readOnly prop's own value never changes across renders.
    const handleMaybePasswordFocus = (maskAwareFocusHandler) => (e) => {
      if (type === "password") {
        e.target.removeAttribute("readonly");
      }
      maskAwareFocusHandler(e);
    };

    // Restore caret after masking
    useLayoutEffect(() => {
      if (!mask) return;
      if (inputLocalRef.current && Number.isInteger(cursor)) {
        try {
          inputLocalRef.current.setSelectionRange(cursor, cursor);
        } catch (_) {}
      }
    }, [mask, displayValue, cursor]);

    // Key handling for masked input
    const handleKeyDownMasked = (e) => {
      if (!mask) return;
      const target = e.target;
      const pos = target.selectionStart || 0;
      const currentValue = displayValue;

      if (e.key === "Backspace") {
        e.preventDefault();
        let newPos = pos - 1;
        while (newPos >= 0 && maskPattern[newPos] !== "9") {
          newPos--;
        }
        if (newPos >= 0) {
          const valArray = (currentValue || getFullPlaceholder()).split("");
          valArray[newPos] = placeholderChar;
          const newValueMasked = valArray.join("");
          setValueAndEmit(newValueMasked);
          setCursor(newPos);
        } else {
          setCursor(0);
        }
        return;
      }

      if (e.key === "Delete") {
        e.preventDefault();
        let newPos = pos;
        while (newPos < maskPattern.length && maskPattern[newPos] !== "9") {
          newPos++;
        }
        if (newPos < maskPattern.length) {
          const valArray = (currentValue || getFullPlaceholder()).split("");
          valArray[newPos] = placeholderChar;
          const newValueMasked = valArray.join("");
          setValueAndEmit(newValueMasked);
          setCursor(newPos);
        }
        return;
      }

      if (e.key.length === 1 && /\d/.test(e.key)) {
        e.preventDefault();
        let newPos = pos;
        while (newPos < maskPattern.length && maskPattern[newPos] !== "9") {
          newPos++;
        }
        if (newPos < maskPattern.length) {
          const valArray = (currentValue || getFullPlaceholder()).split("");
          valArray[newPos] = e.key;
          let nextPos = newPos + 1;
          while (nextPos < maskPattern.length && maskPattern[nextPos] !== "9") {
            nextPos++;
          }
          const newValueMasked = valArray.join("");
          setValueAndEmit(newValueMasked);
          setCursor(nextPos);
        }
      }
      // allow other keys
    };

    const handlePasteMasked = (e) => {
      if (!mask) return;
      e.preventDefault();
      const pasted = e.clipboardData.getData("Text").replace(/\D/g, "");
      if (pasted.length === 0) return;

      const pos = inputLocalRef.current?.selectionStart || 0;
      const currentValue = displayValue;
      const currentDigits = (currentValue || getFullPlaceholder()).replace(
        /[^0-9]/g,
        ""
      );

      let insertPos = 0;
      for (let j = 0; j < pos; j++) {
        if (maskPattern[j] === "9") insertPos++;
      }
      const newDigits =
        currentDigits.substring(0, insertPos) +
        pasted +
        currentDigits.substring(insertPos);
      const digitCount = (maskPattern.match(/9/g) || []).length;
      const limited = newDigits.substring(0, digitCount);
      const newValueMasked = applyMask(limited);
      setValueAndEmit(newValueMasked);

      let newCursor = 0;
      let digitsPlaced = 0;
      for (let j = 0; j < maskPattern.length; j++) {
        if (maskPattern[j] === "9") {
          digitsPlaced++;
          newCursor = j + 1;
          if (digitsPlaced >= insertPos + pasted.length) break;
        }
      }
      setCursor(newCursor);
    };

    // Ensure cursor starts at beginning when clicking an empty masked input
    const handleMouseDownMasked = (e) => {
      if (!mask) return;
      const placeholderValue = getFullPlaceholder();
      const isEffectivelyEmpty =
        !displayValue || displayValue === placeholderValue;
      if (isEffectivelyEmpty) {
        e.preventDefault();
        if (!displayValue) {
          setDisplayValue(placeholderValue);
        }
        setCursor(0);
        // Focus and place caret at the start before the browser sets it to click position
        requestAnimationFrame(() => {
          try {
            inputLocalRef.current?.focus();
            inputLocalRef.current?.setSelectionRange(0, 0);
          } catch (_) {}
        });
      }
    };

    const setValueAndEmit = (newValueMasked) => {
      // Mark that we're typing to prevent useEffect interference
      isTypingRef.current = true;

      setDisplayValue(newValueMasked);
      if (onChange) {
        onChange({ target: { name, value: newValueMasked } });
      }
    };

    // Eye icons for password visibility toggle
    const EyeIcon = () => (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
        <circle cx="12" cy="12" r="3"></circle>
      </svg>
    );

    const EyeOffIcon = () => (
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
        <line x1="1" y1="1" x2="23" y2="23"></line>
      </svg>
    );

    const inputClasses = [
      styles.input,
      styles[variant],
      styles[size],
      isFocused ? styles.focused : "",
      error ? styles.error : "",
      disabled ? styles.disabled : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    const containerClasses = [
      styles.container,
      startIcon ? styles.hasStartIcon : "",
      endIcon || type === "password" ? styles.hasEndIcon : "",
    ]
      .filter(Boolean)
      .join(" ");

    // Determine the actual input type
    const inputType =
      type === "password" ? (showPassword ? "text" : "password") : type;

    return (
      <div className={styles.wrapper}>
        {label && (
          <label
            className={`${styles.label} ${required ? styles.required : ""}`}
          >
            {label}
          </label>
        )}

        <div className={containerClasses}>
          {startIcon && <div className={styles.startIcon}>{startIcon}</div>}

          <input
            ref={setRefs}
            type={inputType}
            name={name}
            placeholder={placeholder}
            value={displayValue}
            onChange={handleChange}
            onKeyDown={mask ? handleKeyDownMasked : undefined}
            onPaste={mask ? handlePasteMasked : undefined}
            onFocus={handleMaybePasswordFocus(
              mask
                ? (e) => {
                    if (!displayValue) {
                      const placeholderValue = getFullPlaceholder();
                      setDisplayValue(placeholderValue);
                      setCursor(0);
                    }
                    setIsFocused(true);
                    if (onFocus) onFocus(e);
                  }
                : handleFocus
            )}
            onBlur={
              mask
                ? (e) => {
                    if (displayValue === getFullPlaceholder()) {
                      setDisplayValue("");
                      if (onChange) {
                        onChange({ target: { name, value: "" } });
                      }
                    }
                    setIsFocused(false);
                    if (onBlur) onBlur(e);
                  }
                : handleBlur
            }
            onMouseDown={mask ? handleMouseDownMasked : undefined}
            disabled={disabled}
            required={required}
            className={inputClasses}
            maxLength={mask ? maskPattern.length : rest?.maxLength}
            readOnly={type === "password" ? true : undefined}
            data-lpignore={type === "password" ? "true" : undefined}
            data-1p-ignore={type === "password" ? "true" : undefined}
            {...rest}
          />

          {type === "password" && (
            <button
              type="button"
              className={styles.passwordToggle}
              onClick={togglePasswordVisibility}
              disabled={disabled}
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          )}

          {endIcon && type !== "password" && (
            <div className={styles.endIcon}>{endIcon}</div>
          )}
        </div>

        {(error || helperText) && (
          <div
            className={`${styles.helperText} ${error ? styles.errorText : ""}`}
          >
            {error || helperText}
          </div>
        )}
      </div>
    );
  }
);

CustomInput.displayName = "CustomInput";

export default CustomInput;
